import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Search,
  Send,
  Building2,
  Phone,
  FileText,
  RefreshCw,
  AlertTriangle,
  Landmark,
  Layers,
  Clock,
} from "lucide-react";
import { DatePicker, Modal, Input, InputNumber, Select, message } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import { dsaApi, fintechApi } from "../services/api";

const { RangePicker } = DatePicker;

// DSA submission status flow:
// LOGGED_IN → UNDER_REVIEW → SANCTIONED / REJECTED → DISBURSED
const STATUS_CONFIG = {
  LOGGED_IN: { label: "Logged In", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  UNDER_REVIEW: { label: "Under Review", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  SANCTIONED: { label: "Sanctioned", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  REJECTED: { label: "Rejected", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  DISBURSED: { label: "Disbursed", bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
};

// Kis status se aage kya ja sakta hai — REJECTED/DISBURSED terminal hain
const NEXT_STATUSES = {
  LOGGED_IN: ["UNDER_REVIEW", "SANCTIONED", "REJECTED"],
  UNDER_REVIEW: ["SANCTIONED", "REJECTED"],
  SANCTIONED: ["DISBURSED", "REJECTED"],
  REJECTED: [],
  DISBURSED: [],
};

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

const formatDateTime = (val) => {
  if (!val) return "-";
  const d = dayjs(val);
  return d.isValid() ? d.format("DD/MM/YYYY hh:mm A") : String(val);
};

// Backend kabhi array, kabhi page object bhejta hai — sab shapes handle
const toArray = (res) =>
  Array.isArray(res)
    ? res
    : Array.isArray(res?.content)
    ? res.content
    : Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res?.data?.content)
    ? res.data.content
    : [];

// Loan file (NBFC wala hi loan record — DSA me ye "file" hai jo lenders ko jaati hai)
const normalizeLoan = (l = {}, idx = 0) => ({
  ...l,
  id: l.id ?? l.loanId ?? idx,
  applicationId: l.applicationId || l.applicationNumber || (l.id ? `#${l.id}` : "-"),
  applicantName: l.applicantName || l.customerName || l.name || "-",
  phone: l.phone || l.contactNumber || l.mobile || "",
  loanType: l.loanType || l.productType || "-",
  amount: Number(l.loanAmount ?? l.amount ?? l.requestedAmount) || 0,
});

// Ek submission = ek file ek lender ke paas
const normalizeSubmission = (s = {}, idx = 0) => {
  const lender = typeof s.lender === "object" ? s.lender : null;
  const product = typeof s.product === "object" ? s.product : null;
  return {
    ...s,
    id: s.id ?? s.submissionId ?? `sub-${idx}`,
    loanId: s.loanId ?? s.loanApplicationId ?? s.loan?.id ?? s.applicationDbId ?? null,
    // Display wala application code (loan row se match karne ke liye nahi)
    applicationCode: s.applicationId || s.applicationNumber || s.loan?.applicationId || "",
    applicantName: s.applicantName || s.customerName || s.loan?.applicantName || "-",
    phone: s.phone || s.contactNumber || s.loan?.phone || "",
    loanType: s.loanType || s.loan?.loanType || "-",
    lenderId: s.lenderId ?? lender?.id ?? null,
    lenderName: s.lenderName || lender?.name || s.bankName || "-",
    productName: s.productName || product?.name || s.lenderProductName || "",
    lenderRefNo:
      s.lenderRefNo || s.lenderReferenceNumber || s.lenderRefNumber || s.referenceNumber || s.refNo || "",
    status: String(s.status || s.submissionStatus || "LOGGED_IN").toUpperCase(),
    requestedAmount: Number(s.requestedAmount ?? s.amount ?? s.loanAmount) || 0,
    sanctionedAmount: Number(s.sanctionedAmount) || 0,
    disbursedAmount: Number(s.disbursedAmount) || 0,
    updatedAt: s.updatedAt || s.lastUpdatedAt || s.statusUpdatedAt || s.modifiedAt || s.createdAt || null,
  };
};

const emptySubmitModal = {
  open: false,
  loan: null,
  lenderId: null,
  productId: null,
  requestedAmount: null,
  lenderRefNo: "",
  remarks: "",
};

const emptyStatusModal = {
  open: false,
  submission: null,
  status: null,
  sanctionedAmount: null,
  interestRate: null,
  sanctionDate: null,
  rejectionReason: "",
  disbursedAmount: null,
  disbursalDate: null,
  remarks: "",
};

export default function DsaPipeline() {
  const { currentTheme } = useTheme();
  const { has } = usePermissions();
  const primaryColor = currentTheme?.color || "#2e856e";

  // Route already DSA_PIPELINE_VIEW se guarded hai — actions ke liye alag code
  // backend par ho bhi sakta hai aur nahi bhi, isliye view permission fallback
  const canAct = has("DSA_PIPELINE_MANAGE") || has("DSA_PIPELINE_VIEW");

  const [loans, setLoans] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [lenders, setLenders] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loansError, setLoansError] = useState(null);
  const [submissionsError, setSubmissionsError] = useState(null);
  const [lendersError, setLendersError] = useState(null);
  const [lendersLoading, setLendersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [lenderFilter, setLenderFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [dateRange, setDateRange] = useState(null);

  // Modals
  const [submitModal, setSubmitModal] = useState(emptySubmitModal);
  const [statusModal, setStatusModal] = useState(emptyStatusModal);

  // Chune hue lender ke products
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);

  // Har keystroke pe API call na ho
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── Lender master (filter dropdown + submit modal dono isi se) ──
  const fetchLenders = useCallback(async () => {
    setLendersLoading(true);
    setLendersError(null);
    try {
      const res = await dsaApi.getLenders({ active: true });
      setLenders(toArray(res));
    } catch (err) {
      console.error("DSA lenders load failed:", err);
      // Koi dummy lender list nahi — dropdown khali rahega, message dikhega
      setLendersError(err?.message || "Failed to load lenders");
      setLenders([]);
    } finally {
      setLendersLoading(false);
    }
  }, []);

  // ── Files + unke lender submissions ──
  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    setLoansError(null);
    setSubmissionsError(null);

    const loanParams = {};
    const subParams = {};
    if (debouncedSearch) loanParams.search = debouncedSearch;
    if (lenderFilter !== "ALL") subParams.lenderId = lenderFilter;
    if (statusFilter !== "ALL") subParams.status = statusFilter;
    if (dateRange && dateRange[0] && dateRange[1]) {
      loanParams.startDate = dateRange[0].format("YYYY-MM-DD");
      loanParams.endDate = dateRange[1].format("YYYY-MM-DD");
      subParams.startDate = loanParams.startDate;
      subParams.endDate = loanParams.endDate;
    }

    const [loanRes, subRes] = await Promise.allSettled([
      fintechApi.getAllLoans(loanParams),
      dsaApi.getSubmissions(subParams),
    ]);

    if (loanRes.status === "fulfilled") {
      setLoans(toArray(loanRes.value).map(normalizeLoan));
    } else {
      console.error("Loan files load failed:", loanRes.reason);
      setLoansError(loanRes.reason?.message || "Failed to load loan files");
      setLoans([]);
    }

    if (subRes.status === "fulfilled") {
      setSubmissions(toArray(subRes.value).map(normalizeSubmission));
    } else {
      console.error("DSA submissions load failed:", subRes.reason);
      // DSA endpoints abhi proposed hain — fail hone par mock nahi, saaf error
      setSubmissionsError(subRes.reason?.message || "Failed to load lender submissions");
      setSubmissions([]);
    }

    setLoading(false);
  }, [debouncedSearch, lenderFilter, statusFilter, dateRange]);

  useEffect(() => {
    fetchLenders();
  }, [fetchLenders]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  const retryAll = () => {
    fetchPipeline();
    if (lendersError) fetchLenders();
  };

  // Lender id → naam (submission me naam na aaye to master se uthao)
  const lenderNameById = useMemo(() => {
    const map = {};
    lenders.forEach((l) => {
      const id = l?.id ?? l?.lenderId;
      if (id !== undefined && id !== null) map[String(id)] = l?.name || l?.lenderName || l?.bankName || "";
    });
    return map;
  }, [lenders]);

  const resolveLenderName = useCallback(
    (sub) => {
      if (sub.lenderName && sub.lenderName !== "-") return sub.lenderName;
      const fromMaster = sub.lenderId !== null ? lenderNameById[String(sub.lenderId)] : "";
      return fromMaster || "-";
    },
    [lenderNameById]
  );

  // ── File-wise grouping: ek application row, uske neeche uske lender submissions ──
  const groups = useMemo(() => {
    const filterActive = lenderFilter !== "ALL" || statusFilter !== "ALL";
    const term = debouncedSearch.toLowerCase();

    const matchesSearch = (g) =>
      term === "" ||
      g.applicantName?.toLowerCase().includes(term) ||
      String(g.phone || "").toLowerCase().includes(term) ||
      String(g.applicationId || "").toLowerCase().includes(term);

    const byLoanId = new Map();
    loans.forEach((l) => {
      byLoanId.set(String(l.id), {
        key: `loan-${l.id}`,
        loan: l,
        loanId: l.id,
        applicationId: l.applicationId,
        applicantName: l.applicantName,
        phone: l.phone,
        loanType: l.loanType,
        amount: l.amount,
        submissions: [],
        orphan: false,
      });
    });

    submissions.forEach((s) => {
      const key = s.loanId !== null && s.loanId !== undefined ? String(s.loanId) : null;
      const existing = key ? byLoanId.get(key) : null;
      if (existing) {
        existing.submissions.push(s);
        return;
      }
      // Loan list me file nahi mili (filter/permission ya dusra page) — submission
      // ke apne fields se hi row banate hain, kuch invent nahi karte
      const orphanKey = key || `sub-${s.id}`;
      if (!byLoanId.has(orphanKey)) {
        byLoanId.set(orphanKey, {
          key: `orphan-${orphanKey}`,
          loan: null,
          loanId: s.loanId ?? null,
          applicationId: s.applicationCode || "-",
          applicantName: s.applicantName,
          phone: s.phone,
          loanType: s.loanType,
          amount: s.requestedAmount,
          submissions: [],
          orphan: true,
        });
      }
      byLoanId.get(orphanKey).submissions.push(s);
    });

    let list = Array.from(byLoanId.values());

    // Lender/status filter lagne par sirf matching submissions wali files dikhao
    if (filterActive) list = list.filter((g) => g.submissions.length > 0);

    // Safety net — backend search param ignore kare to bhi list sahi dikhe
    list = list.filter(matchesSearch);

    // Jinke paas submissions hain woh upar
    list.sort((a, b) => b.submissions.length - a.submissions.length);
    return list;
  }, [loans, submissions, lenderFilter, statusFilter, debouncedSearch]);

  const totalSubmissions = groups.reduce((sum, g) => sum + g.submissions.length, 0);

  const resetFilters = () => {
    setSearchTerm("");
    setLenderFilter("ALL");
    setStatusFilter("ALL");
    setDateRange(null);
  };

  // ── Submit to Lender ──
  const openSubmitModal = (group) => {
    if (!group.loanId) {
      message.warning("This file has no loan id — cannot submit to a lender");
      return;
    }
    setProducts([]);
    setProductsError(null);
    setSubmitModal({
      ...emptySubmitModal,
      open: true,
      loan: {
        id: group.loanId,
        applicationId: group.applicationId,
        applicantName: group.applicantName,
        loanType: group.loanType,
        amount: group.amount,
      },
      requestedAmount: group.amount || null,
    });
  };

  const handleLenderChange = async (lenderId) => {
    setSubmitModal((prev) => ({ ...prev, lenderId, productId: null }));
    setProducts([]);
    setProductsError(null);
    if (!lenderId) return;
    setProductsLoading(true);
    try {
      const res = await dsaApi.getLenderProducts(lenderId);
      setProducts(toArray(res));
    } catch (err) {
      console.error("Lender products load failed:", err);
      setProductsError(err?.message || "Failed to load lender products");
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleSubmitToLender = async () => {
    const { loan, lenderId, productId, requestedAmount, lenderRefNo, remarks } = submitModal;
    if (!loan) return;
    if (!lenderId) {
      message.warning("Please select a lender");
      return;
    }
    // Product tabhi zaroori hai jab list load hui ho — API fail hui to block mat karo
    if (products.length > 0 && !productId) {
      message.warning("Please select a lender product");
      return;
    }
    if (!(Number(requestedAmount) > 0)) {
      message.warning("Please enter a valid requested amount");
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        lenderId,
        requestedAmount: Number(requestedAmount),
        status: "LOGGED_IN",
      };
      if (productId) payload.productId = productId;
      if (lenderRefNo.trim()) payload.lenderRefNo = lenderRefNo.trim();
      if (remarks.trim()) payload.remarks = remarks.trim();

      await dsaApi.submitToLender(loan.id, payload);
      // Success sirf API pass hone par
      message.success(`File ${loan.applicationId} submitted to lender successfully`);
      setSubmitModal(emptySubmitModal);
      setProducts([]);
      fetchPipeline();
    } catch (err) {
      console.error("Submit to lender failed:", err);
      message.error(err?.message || "Failed to submit file to lender");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Update submission status ──
  const openStatusModal = (submission) => {
    const allowed = NEXT_STATUSES[submission.status] || [];
    if (allowed.length === 0) {
      message.info("This submission is already at a final status");
      return;
    }
    setStatusModal({
      ...emptyStatusModal,
      open: true,
      submission,
      status: allowed[0],
      sanctionedAmount: submission.sanctionedAmount || submission.requestedAmount || null,
      disbursedAmount: submission.sanctionedAmount || null,
      sanctionDate: dayjs(),
      disbursalDate: dayjs(),
    });
  };

  const handleUpdateStatus = async () => {
    const s = statusModal.submission;
    if (!s || !statusModal.status) return;

    const payload = { status: statusModal.status };

    if (statusModal.status === "SANCTIONED") {
      if (!(Number(statusModal.sanctionedAmount) > 0)) {
        message.warning("Please enter a valid sanctioned amount");
        return;
      }
      if (!statusModal.sanctionDate) {
        message.warning("Please select the sanction date");
        return;
      }
      payload.sanctionedAmount = Number(statusModal.sanctionedAmount);
      if (statusModal.interestRate !== null && statusModal.interestRate !== undefined) {
        payload.interestRate = Number(statusModal.interestRate);
      }
      payload.sanctionDate = statusModal.sanctionDate.format("YYYY-MM-DD");
    }

    if (statusModal.status === "REJECTED") {
      if (!statusModal.rejectionReason.trim()) {
        message.warning("Please provide a rejection reason");
        return;
      }
      payload.rejectionReason = statusModal.rejectionReason.trim();
    }

    if (statusModal.status === "DISBURSED") {
      if (!(Number(statusModal.disbursedAmount) > 0)) {
        message.warning("Please enter a valid disbursed amount");
        return;
      }
      if (!statusModal.disbursalDate) {
        message.warning("Please select the disbursal date");
        return;
      }
      payload.disbursedAmount = Number(statusModal.disbursedAmount);
      payload.disbursalDate = statusModal.disbursalDate.format("YYYY-MM-DD");
    }

    if (statusModal.remarks.trim()) payload.remarks = statusModal.remarks.trim();

    setActionLoading(true);
    try {
      await dsaApi.updateSubmissionStatus(s.id, payload);
      const label = STATUS_CONFIG[statusModal.status]?.label || statusModal.status;
      message.success(`${resolveLenderName(s)} submission marked as ${label}`);
      setStatusModal(emptyStatusModal);
      fetchPipeline();
    } catch (err) {
      console.error("Update submission status failed:", err);
      message.error(err?.message || "Failed to update submission status");
    } finally {
      setActionLoading(false);
    }
  };

  const allowedNextStatuses = statusModal.submission
    ? NEXT_STATUSES[statusModal.submission.status] || []
    : [];

  const hardError = loansError && submissionsError;

  return (
    <div className="space-y-6 pb-12">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
            style={{ backgroundColor: primaryColor }}
          >
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">DSA Pipeline</h1>
            <p className="text-xs md:text-sm text-gray-500">
              Loan files sourced for partner banks / NBFCs — track every lender submission separately
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!loading && !hardError && (
            <span className="text-xs font-semibold text-gray-500">
              {groups.length} file{groups.length === 1 ? "" : "s"} · {totalSubmissions} submission
              {totalSubmissions === 1 ? "" : "s"}
            </span>
          )}
          <button
            onClick={retryAll}
            disabled={loading}
            className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all shadow-sm"
            title="Refresh Pipeline"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* ── Partial failure banners — jo load nahi hua, saaf bataya jaye ── */}
      {!loading && !hardError && (loansError || submissionsError || lendersError) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-800 space-y-1">
              {loansError && <div>Loan files could not be loaded: {loansError}</div>}
              {submissionsError && <div>Lender submissions could not be loaded: {submissionsError}</div>}
              {lendersError && <div>Lender master could not be loaded: {lendersError}</div>}
            </div>
          </div>
          <button
            onClick={retryAll}
            className="px-3 py-1.5 rounded-xl text-xs font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 inline-flex items-center gap-1.5 shrink-0"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div className="bg-white p-4 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Search Box */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search Name, Phone, App ID..."
              className="w-full pl-10 pr-4 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
            />
          </div>

          {/* Lender Dropdown — sirf active lenders (master API se) */}
          <div>
            <select
              value={lenderFilter}
              onChange={(e) => setLenderFilter(e.target.value)}
              disabled={lendersLoading || lenders.length === 0}
              className="w-full px-3 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e] font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="ALL">
                {lendersLoading
                  ? "Loading lenders..."
                  : lenders.length === 0
                  ? "No lenders available"
                  : "All Lenders"}
              </option>
              {lenders.map((l) => {
                const id = l?.id ?? l?.lenderId;
                return (
                  <option key={id} value={id}>
                    {l?.name || l?.lenderName || l?.bankName || `Lender #${id}`}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Status Dropdown */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e] font-medium text-gray-700"
            >
              <option value="ALL">All Statuses</option>
              {Object.keys(STATUS_CONFIG).map((key) => (
                <option key={key} value={key}>
                  {STATUS_CONFIG[key].label}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range Picker */}
          <div>
            <RangePicker
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              format="DD/MM/YYYY"
              placeholder={["Start Date", "End Date"]}
              className="w-full rounded-xl border-gray-200 py-1.5 shadow-none"
            />
          </div>

          {/* Quick Clear Button */}
          <div className="flex items-center">
            <button
              onClick={resetFilters}
              className="w-full px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* ── Pipeline Table ── */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: primaryColor, borderTopColor: "transparent" }}
            />
          </div>
        ) : hardError ? (
          <div className="flex flex-col items-center text-center gap-3 py-16 px-6">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
            <h2 className="text-base font-bold text-gray-900">Could not load the DSA pipeline</h2>
            <p className="text-sm text-gray-500 max-w-md">
              {submissionsError}
              {loansError ? ` · ${loansError}` : ""}
            </p>
            <button
              onClick={retryAll}
              className="mt-1 px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                  <th className="py-4 px-4">Application ID</th>
                  <th className="py-4 px-4">Applicant</th>
                  <th className="py-4 px-4">Phone</th>
                  <th className="py-4 px-4">Loan Type</th>
                  <th className="py-4 px-4 text-right">Requested Amount</th>
                  <th className="py-4 px-4">Lender</th>
                  <th className="py-4 px-4">Lender Ref No.</th>
                  <th className="py-4 px-4 text-center">Status</th>
                  <th className="py-4 px-4 text-right">Sanctioned Amount</th>
                  <th className="py-4 px-4">Last Updated</th>
                  <th className="py-4 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {groups.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-12 text-center text-gray-400">
                      <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-semibold">No loan files in the pipeline</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Try changing filters or search terms
                      </p>
                    </td>
                  </tr>
                ) : (
                  groups.map((g) => (
                    <React.Fragment key={g.key}>
                      {/* ── File row (application level) ── */}
                      <tr className="bg-gray-50/40 hover:bg-gray-50 transition-colors">
                        <td className="py-3.5 px-4 font-bold text-gray-900">
                          <span className="font-mono text-xs px-2 py-1 bg-gray-100 rounded-lg">
                            {g.applicationId}
                          </span>
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="font-semibold text-gray-900">{g.applicantName}</div>
                          {g.orphan && (
                            <div className="text-[11px] text-amber-600 font-semibold mt-0.5">
                              File details from submission record
                            </div>
                          )}
                        </td>
                        <td className="py-3.5 px-4 text-xs text-gray-600">
                          {g.phone ? (
                            <span className="inline-flex items-center gap-1">
                              <Phone className="w-3 h-3 text-gray-400" />
                              {g.phone}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700">
                            {g.loanType}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-right font-black text-gray-900">
                          {formatINR(g.amount)}
                        </td>
                        {/* Lender-level columns file row par summary dikhate hain */}
                        <td className="py-3.5 px-4 text-xs font-semibold text-gray-500" colSpan={5}>
                          {g.submissions.length === 0 ? (
                            <span className="text-gray-400 italic">Not submitted to any lender yet</span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-gray-600">
                              <Landmark className="w-3.5 h-3.5 text-gray-400" />
                              {g.submissions.length} lender submission
                              {g.submissions.length === 1 ? "" : "s"}
                            </span>
                          )}
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          {canAct && (
                            <button
                              onClick={() => openSubmitModal(g)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white rounded-xl shadow-sm transition-all hover:brightness-105 active:scale-95"
                              style={{ backgroundColor: primaryColor }}
                              title="Submit this file to a lender"
                            >
                              <Send className="w-3.5 h-3.5" />
                              Submit to Lender
                            </button>
                          )}
                        </td>
                      </tr>

                      {/* ── Uske lender submissions (indent ke saath) ── */}
                      {g.submissions.map((s) => {
                        const statusInfo = STATUS_CONFIG[s.status] || {
                          label: s.status,
                          bg: "bg-gray-100",
                          text: "text-gray-700",
                          border: "border-gray-200",
                        };
                        const terminal = (NEXT_STATUSES[s.status] || []).length === 0;
                        return (
                          <tr key={s.id} className="hover:bg-gray-50/60 transition-colors">
                            <td className="py-3 px-4" />
                            <td className="py-3 px-4" />
                            <td className="py-3 px-4" />
                            <td className="py-3 px-4" />
                            <td className="py-3 px-4 text-right text-xs text-gray-500">
                              {s.requestedAmount > 0 ? formatINR(s.requestedAmount) : "-"}
                            </td>
                            <td className="py-3 px-4 border-l-2 border-[#2e856e]/30">
                              <div className="flex items-center gap-1.5">
                                <Building2 className="w-3.5 h-3.5 text-gray-400" />
                                <span className="font-semibold text-gray-800 text-xs">
                                  {resolveLenderName(s)}
                                </span>
                              </div>
                              {s.productName && (
                                <div className="text-[11px] text-gray-400 mt-0.5 pl-5">{s.productName}</div>
                              )}
                            </td>
                            <td className="py-3 px-4 text-xs font-mono text-gray-600">
                              {s.lenderRefNo || <span className="text-gray-300">-</span>}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ${statusInfo.bg} ${statusInfo.text} ${statusInfo.border}`}
                              >
                                {statusInfo.label}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right text-xs font-bold text-gray-900">
                              {s.sanctionedAmount > 0 ? formatINR(s.sanctionedAmount) : "-"}
                              {s.disbursedAmount > 0 && (
                                <div className="text-[11px] font-semibold text-purple-600 mt-0.5">
                                  Disbursed {formatINR(s.disbursedAmount)}
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4 text-xs text-gray-500 whitespace-nowrap">
                              {formatDateTime(s.updatedAt)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {canAct && !terminal ? (
                                <button
                                  onClick={() => openStatusModal(s)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl border border-gray-200 transition-all"
                                  title="Update lender status"
                                >
                                  <Clock className="w-3.5 h-3.5" />
                                  Update Status
                                </button>
                              ) : (
                                <span className="text-[11px] text-gray-400 font-semibold">
                                  {terminal ? "Closed" : ""}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !hardError && groups.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 text-xs font-semibold text-gray-500">
            Showing {groups.length} file{groups.length === 1 ? "" : "s"} with {totalSubmissions} lender
            submission{totalSubmissions === 1 ? "" : "s"}
          </div>
        )}
      </div>

      {/* ── Submit to Lender Modal ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-[#2e856e] font-bold text-base">
            <Send className="w-5 h-5" />
            <span>Submit File to Lender</span>
          </div>
        }
        open={submitModal.open}
        onOk={handleSubmitToLender}
        onCancel={() => {
          setSubmitModal(emptySubmitModal);
          setProducts([]);
          setProductsError(null);
        }}
        okText="Submit File"
        confirmLoading={actionLoading}
        okButtonProps={{ style: { backgroundColor: "#2e856e", borderColor: "#2e856e" } }}
      >
        {submitModal.loan && (
          <div className="space-y-4 py-3 text-sm">
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs">
              <div className="font-bold text-gray-800">{submitModal.loan.applicantName}</div>
              <div className="text-gray-500">
                App ID: {submitModal.loan.applicationId} | Type: {submitModal.loan.loanType}
              </div>
              {/* Ek hi file kai lenders ko ja sakti hai — dobara submit karna allowed hai */}
              <div className="text-[11px] text-gray-400 mt-1">
                Same file can be logged in with multiple lenders.
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Lender *</label>
              <Select
                className="w-full"
                placeholder={lendersLoading ? "Loading lenders..." : "Select active lender"}
                value={submitModal.lenderId}
                onChange={handleLenderChange}
                loading={lendersLoading}
                disabled={lenders.length === 0}
                options={lenders.map((l) => {
                  const id = l?.id ?? l?.lenderId;
                  return {
                    value: id,
                    label: l?.name || l?.lenderName || l?.bankName || `Lender #${id}`,
                  };
                })}
              />
              {lendersError && (
                <p className="text-[11px] text-red-600 mt-1">
                  {lendersError} —{" "}
                  <button onClick={fetchLenders} className="underline font-semibold">
                    retry
                  </button>
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Lender Product</label>
              <Select
                className="w-full"
                placeholder={
                  !submitModal.lenderId
                    ? "Select a lender first"
                    : productsLoading
                    ? "Loading products..."
                    : products.length === 0
                    ? "No products available"
                    : "Select product"
                }
                value={submitModal.productId}
                onChange={(val) => setSubmitModal((prev) => ({ ...prev, productId: val }))}
                loading={productsLoading}
                disabled={!submitModal.lenderId || products.length === 0}
                options={products.map((p) => {
                  const id = p?.id ?? p?.productId;
                  return {
                    value: id,
                    label: p?.name || p?.productName || p?.loanType || `Product #${id}`,
                  };
                })}
              />
              {productsError && <p className="text-[11px] text-red-600 mt-1">{productsError}</p>}
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Requested Amount (₹) *</label>
              <InputNumber
                className="w-full rounded-xl"
                min={1}
                value={submitModal.requestedAmount}
                onChange={(val) => setSubmitModal((prev) => ({ ...prev, requestedAmount: val }))}
                formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Lender Reference No. (optional at login)
              </label>
              <Input
                className="rounded-xl"
                placeholder="e.g. HDFC/PL/2024/88213"
                value={submitModal.lenderRefNo}
                onChange={(e) => setSubmitModal((prev) => ({ ...prev, lenderRefNo: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Remarks</label>
              <Input.TextArea
                rows={3}
                className="rounded-xl"
                placeholder="Login notes, documents shared, RM name..."
                value={submitModal.remarks}
                onChange={(e) => setSubmitModal((prev) => ({ ...prev, remarks: e.target.value }))}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* ── Update Submission Status Modal ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-gray-800 font-bold text-base">
            <Landmark className="w-5 h-5" />
            <span>Update Lender Status</span>
          </div>
        }
        open={statusModal.open}
        onOk={handleUpdateStatus}
        onCancel={() => setStatusModal(emptyStatusModal)}
        okText="Save Update"
        confirmLoading={actionLoading}
        okButtonProps={
          statusModal.status === "REJECTED"
            ? { danger: true }
            : { style: { backgroundColor: "#2e856e", borderColor: "#2e856e" } }
        }
      >
        {statusModal.submission && (
          <div className="space-y-4 py-3 text-sm">
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs">
              <div className="font-bold text-gray-800">{resolveLenderName(statusModal.submission)}</div>
              <div className="text-gray-500">
                {statusModal.submission.applicantName}
                {statusModal.submission.lenderRefNo ? ` | Ref: ${statusModal.submission.lenderRefNo}` : ""}
              </div>
              <div className="text-gray-400 mt-1">
                Current status:{" "}
                {STATUS_CONFIG[statusModal.submission.status]?.label || statusModal.submission.status}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">New Status *</label>
              <Select
                className="w-full"
                value={statusModal.status}
                onChange={(val) => setStatusModal((prev) => ({ ...prev, status: val }))}
                options={allowedNextStatuses.map((st) => ({
                  value: st,
                  label: STATUS_CONFIG[st]?.label || st,
                }))}
              />
            </div>

            {/* SANCTIONED — amount, rate, date */}
            {statusModal.status === "SANCTIONED" && (
              <>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Sanctioned Amount (₹) *
                  </label>
                  <InputNumber
                    className="w-full rounded-xl"
                    min={1}
                    value={statusModal.sanctionedAmount}
                    onChange={(val) => setStatusModal((prev) => ({ ...prev, sanctionedAmount: val }))}
                    formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Interest Rate (% p.a.)
                    </label>
                    <InputNumber
                      className="w-full rounded-xl"
                      min={1}
                      max={40}
                      step={0.25}
                      value={statusModal.interestRate}
                      onChange={(val) => setStatusModal((prev) => ({ ...prev, interestRate: val }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Sanction Date *</label>
                    <DatePicker
                      className="w-full rounded-xl"
                      format="DD/MM/YYYY"
                      value={statusModal.sanctionDate}
                      onChange={(val) => setStatusModal((prev) => ({ ...prev, sanctionDate: val }))}
                    />
                  </div>
                </div>
              </>
            )}

            {/* REJECTED — reason mandatory */}
            {statusModal.status === "REJECTED" && (
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Rejection Reason *</label>
                <Input.TextArea
                  rows={3}
                  className="rounded-xl"
                  placeholder="e.g. Low CIBIL, negative profile, policy mismatch..."
                  value={statusModal.rejectionReason}
                  onChange={(e) =>
                    setStatusModal((prev) => ({ ...prev, rejectionReason: e.target.value }))
                  }
                />
              </div>
            )}

            {/* DISBURSED — amount + date (commission isi par banti hai) */}
            {statusModal.status === "DISBURSED" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Disbursed Amount (₹) *
                  </label>
                  <InputNumber
                    className="w-full rounded-xl"
                    min={1}
                    value={statusModal.disbursedAmount}
                    onChange={(val) => setStatusModal((prev) => ({ ...prev, disbursedAmount: val }))}
                    formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Disbursal Date *</label>
                  <DatePicker
                    className="w-full rounded-xl"
                    format="DD/MM/YYYY"
                    value={statusModal.disbursalDate}
                    onChange={(val) => setStatusModal((prev) => ({ ...prev, disbursalDate: val }))}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Remarks</label>
              <Input.TextArea
                rows={2}
                className="rounded-xl"
                placeholder="Lender RM feedback, pending documents..."
                value={statusModal.remarks}
                onChange={(e) => setStatusModal((prev) => ({ ...prev, remarks: e.target.value }))}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
